import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Clock, Users, Star, Award } from 'lucide-react'
import { DanceClass } from '../core'

interface ClassCardProps {
  danceClass: DanceClass
  onBook: () => void
  onDemo: () => void
}

export default function ClassCard({ danceClass, onBook, onDemo }: ClassCardProps) {
  const navigate = useNavigate()

  const getCategoryImage = (category: string) => {
    switch (category) {
      case 'special':
        return '/images/classes/special-dance.png'
      case 'classical':
        return '/images/classes/classical-dance.png'
      case 'seniors':
        return '/images/classes/seniors-dance.png'
      case 'fitness':
        return '/images/classes/fitness-dance.jpeg'
      case 'choreography':
        return '/images/classes/choreography-dance.png'
      case 'art':
        return '/images/classes/art-craft.png'
      default:
        return '/images/classes/default-dance.jpg'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'special':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
      case 'classical':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
      case 'seniors':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'fitness':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      case 'choreography':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
      case 'art':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
    }
  }

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
      className="card card-hover group h-full flex flex-col"
    >
      {/* Class Image */}
      <div 
        className="relative h-48 bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-6xl text-white overflow-hidden bg-cover bg-center bg-no-repeat cursor-pointer"
        style={{ 
          backgroundImage: `url("${getCategoryImage(danceClass.category)}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        onClick={() => navigate(`/classes/${danceClass.id}`)}
      >
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Category Badge */}
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(danceClass.category)}`}>
            {danceClass.category}
          </span>
        </div>

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-2xl mb-2">✨</div>
            <div className="text-sm font-medium">Click to learn more</div>
          </div>
        </div>
      </div>

      {/* Class Content */}
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-300 transition-colors line-clamp-2">
          {danceClass.name}
        </h3>
        
        <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
          {danceClass.description}
        </p>

        {/* Class Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-gray-600 dark:text-gray-300 text-sm">
            <Clock className="w-4 h-4 mr-2 text-primary-500" />
            <span>{danceClass.duration} minutes</span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-300 text-sm">
            <Users className="w-4 h-4 mr-2 text-primary-500" />
            <span>Max {danceClass.maxCapacity} students</span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-300 text-sm">
            <Award className="w-4 h-4 mr-2 text-primary-500" />
            <span className="font-semibold text-primary-600 dark:text-primary-300">{danceClass.level}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 mt-auto">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onBook}
              className="btn btn-primary text-sm py-2"
            >
              Book Now
            </button>
            <button
              onClick={() => navigate(`/classes/${danceClass.id}`)}
              className="btn btn-outline text-sm py-2"
            >
              Learn More
            </button>
          </div>
          
          
          <button
            onClick={onDemo}
            className="w-full btn bg-green-600 hover:bg-green-700 text-white text-sm py-2"
          >
            <Star className="w-4 h-4 mr-2" />
            Try Free Demo Session
          </button>
        </div>

      </div>
    </motion.div>
  )
}
